// CRM web shell — React Router v7 route tree.
//
// Layout tree:
//
//   /login          → LoginPage (no shell)
//   *               → NotFoundPage (no shell)
//   /               → <Navigate to="/dashboard" />
//   <AppLayout>     ← persistent shell (AuthGuard + Sidebar + Topbar + Outlet)
//     /dashboard    → DashboardPage
//     /deals        → DealsPage
//     /contacts     → ContactsPage
//     /templates    → TemplatesPage
//     /hot-leads    → HotLeadsPage
//     /settings     → SettingsPage
//
// Design notes:
//   - AuthGuard lives INSIDE AppLayout so both the redirect-to-login and the
//     auth-loading <Spinner/> reuse the shell's viewport sizing rules.
//   - react-router v7 `<Outlet />` + nested routes keep the sidebar mounted
//     across navigation (no remount = no lost chat/search state later).

import type { ReactElement } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { AppLayout } from './components/layout/AppLayout.js';
import ContactsPage from './pages/ContactsPage.js';
import ContactDetailPage from './pages/ContactDetailPage.js';
import { ContactCardPage } from './features/contacts/pages/ContactCardPage.js';
import DashboardPage from './pages/DashboardPage.js';
import DealDetailPage from './pages/DealDetailPage.js';
import DealsPage from './pages/DealsPage.js';
import HotLeadsPage from './pages/HotLeadsPage.js';
import LoginPage from './pages/LoginPage.js';
import MemoryPage from './pages/MemoryPage.js';
import NotFoundPage from './pages/NotFoundPage.js';
import OperatorModulePage from './pages/OperatorModulePage.js';
import SettingsPage from './pages/SettingsPage.js';
import TemplatesPage from './pages/TemplatesPage.js';
import { DocumentsPage } from './features/documents/pages/DocumentsPage.js';
import { DocumentCardPage } from './features/documents/pages/DocumentCardPage.js';
import { useParams } from 'react-router-dom';

function RootRedirect(): ReactElement {
  return <Navigate to="/dashboard" replace />;
}

function DocumentCardWrapper(): ReactElement {
  const { documentId } = useParams();
  if (!documentId) return <Navigate to="/documents" replace />;
  return <DocumentCardPage documentId={documentId} />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <RootRedirect /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/deals', element: <DealsPage /> },
      { path: '/deals/:dealId', element: <DealDetailPage /> },
      { path: '/contacts', element: <ContactsPage /> },
      { path: '/contacts/mvp', element: <ContactCardPage /> },
      { path: '/contacts/:contactId', element: <ContactDetailPage /> },
      { path: '/templates', element: <TemplatesPage /> },
      { path: '/documents', element: <DocumentsPage /> },
      {
        path: '/documents/:documentId',
        element: <DocumentCardWrapper />,
      },
      { path: '/hot-leads', element: <HotLeadsPage /> },
      { path: '/memory', element: <MemoryPage /> },
      { path: '/run-center', element: <OperatorModulePage title="Run Center" /> },
      { path: '/scenario-editor', element: <OperatorModulePage title="Scenario Editor" /> },
      { path: '/prompt-compiler', element: <OperatorModulePage title="Prompt Compiler" /> },
      { path: '/knowledge', element: <OperatorModulePage title="Knowledge Base" /> },
      { path: '/files', element: <OperatorModulePage title="Files" /> },
      { path: '/agents', element: <OperatorModulePage title="Agent Monitor" /> },
      { path: '/opencode', element: <OperatorModulePage title="OpenCode" /> },
      { path: '/settings', element: <SettingsPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
